import type { AnyAbility } from '@casl/ability'
import type { NavGroup, NavLink, NavSectionTitle, VerticalNavItemsType } from 'src/@core/layouts/types'
import type { UserDataType } from 'src/context/types'

export function isNavSectionTitle(item: NavLink | NavGroup | NavSectionTitle): item is NavSectionTitle {
  return 'sectionTitle' in item && Boolean((item as NavSectionTitle).sectionTitle)
}

export function isNavGroup(item: NavLink | NavGroup | NavSectionTitle): item is NavGroup {
  return 'children' in item && Boolean((item as NavGroup).children)
}

export type DrillFrame = {
  title: string
  items: VerticalNavItemsType
}

/**
 * Drops items the user cannot see; collapses groups with no visible children into a lone link when the group has `path`.
 */
export function filterSubNavTreeByAcl(
  items: VerticalNavItemsType,
  canShow: (item: NavLink) => boolean
): VerticalNavItemsType {
  const out: VerticalNavItemsType = []

  for (const raw of items) {
    if (isNavSectionTitle(raw)) continue

    if (isNavGroup(raw)) {
      const g = raw
      const filteredChildren = g.children?.length
        ? filterSubNavTreeByAcl(g.children as VerticalNavItemsType, canShow)
        : []

      if (filteredChildren.length > 0) {
        out.push({ ...g, children: filteredChildren as NavGroup['children'] })
      } else if (g.path && canShow(g as NavLink)) {
        out.push({
          title: g.title,
          path: g.path,
          icon: g.icon,
          subject: g.subject,
          action: g.action,
          auth: g.auth
        } as NavLink)
      }
    } else if (canShow(raw as NavLink)) {
      out.push(raw)
    }
  }

  return out
}

/** ACL-aware visibility check aligned with {@link CanViewNavLink}. */
export function canShowNavLinkItem(item: NavLink, ability: AnyAbility | undefined, user: UserDataType | null): boolean {
  if (item.auth === false) return true
  if (item.customPermissionCheck && user) return item.customPermissionCheck(user)

  return Boolean(ability && ability.can(item.action, item.subject))
}

/** First navigable leaf in tree order (respects ACL / customPermissionCheck). */
export function findFirstVisibleNavLinkInTree(
  items: VerticalNavItemsType,
  ability: AnyAbility | undefined,
  user: UserDataType | null
): NavLink | null {
  for (const raw of items) {
    if (isNavSectionTitle(raw)) continue

    if (isNavGroup(raw)) {
      const g = raw
      if (g.path && canShowNavLinkItem(g as NavLink, ability, user)) {
        return g as NavLink
      }
      const nested = findFirstVisibleNavLinkInTree(g.children ?? [], ability, user)
      if (nested) return nested
      continue
    }

    const link = raw as NavLink
    if (link.path && canShowNavLinkItem(link, ability, user)) {
      return link
    }
  }

  return null
}
